import { Button, Modal, Input, Form, Row, Col, DatePicker } from "antd";
import { FC, useEffect } from "react";
import dayjs from "dayjs";

interface PrintDeliveryOrderModalProps {
  isVisible: boolean;
  onClose: () => void;
  onPrint: (bondedDetails: string, date: string) => void;
  salesOrder: string;
  packageId: string;
  shippingMethod?: string | null;
  date?: string | null;
  bondedDetails?: string | null;
  customerAddress?: any;
  loading?: boolean;
}

const PrintDeliveryOrderModal: FC<PrintDeliveryOrderModalProps> = ({
  isVisible,
  onClose,
  onPrint,
  salesOrder,
  packageId,
  shippingMethod,
  date,
  bondedDetails,
  customerAddress,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const bondedValue = Form.useWatch("bonded", form);
  const dateValue = Form.useWatch("date", form);

  // Format customer address for display
  const formatCustomerAddress = () => {
    if (!customerAddress) {
      return "N/A";
    }
    const parts = [
      customerAddress.addr1,
      customerAddress.addr2,
      customerAddress.addr3,
      customerAddress.addr4,
      customerAddress.city ? `${customerAddress.city}${customerAddress.state ? `, ${customerAddress.state}` : ""}${customerAddress.postalCode ? ` ${customerAddress.postalCode}` : ""}` : "",
      customerAddress.country,
    ].filter((part) => part && part.trim() !== "");
    return parts.length > 0 ? parts.join("\n") : "N/A";
  };

  useEffect(() => {
    if (isVisible) {
      // Set default date to today's date
      const defaultDate = dayjs();
      
      // Prefill bondedDetails and date if available, otherwise reset fields
      if (bondedDetails) {
        form.setFieldsValue({
          bonded: bondedDetails,
          date: defaultDate,
        });
      } else {
        form.setFieldsValue({
          date: defaultDate,
        });
      }
    }
  }, [isVisible, form, bondedDetails]);

  const handlePrint = () => {
    form.validateFields().then((values) => {
      // Format date as YYYY-MM-DD string
      const formattedDate = values.date ? dayjs(values.date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
      onPrint(values.bonded, formattedDate);
    });
  };

  return (
    <Modal
      title={`Print Delivery Order — Sales Order ${salesOrder}`}
      open={isVisible}
      maskClosable={false}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="print"
          type="primary"
          onClick={handlePrint}
          loading={loading}
          disabled={!bondedValue || bondedValue.trim() === "" || !dateValue}
        >
          Print / Download
        </Button>,
      ]}
      width={900}
      destroyOnClose
      centered
    >
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          {/* Customer Address - Big, and Bonded field in the same line */}
          <Col span={12} style={{ marginBottom: 16 }}>
            <label style={{ 
              display: "block", 
              marginBottom: 4, 
              fontSize: "14px", 
              fontWeight: 500 
            }}>
              Customer address (BILL TO)
            </label>
            <div style={{ 
              padding: "12px 16px", 
              backgroundColor: "#f5f5f5", 
              borderRadius: "4px",
              minHeight: "80px",
              border: "1px solid #d9d9d9",
              fontSize: "16px",
              lineHeight: "1.8",
              whiteSpace: "pre-line"
            }}>
              {formatCustomerAddress()}
            </div>
          </Col>

          {/* Bonded field - Same line as address */}
          <Col span={12} style={{ marginBottom: 16 }}>
            <Form.Item
              label={
                <span>
                  Bonded
                </span>
              }
              name="bonded"
              rules={[
                { required: true, message: "Bonded details are required" },
              ]}
              style={{ marginBottom: 4 }}
            >
              <Input
                size="large"
                placeholder="Enter bonded details"
                style={{ border: "2px solid #1890ff" }}
              />
            </Form.Item>
            <div style={{ 
              fontSize: "12px", 
              color: "#666", 
              marginTop: 4
            }}>
              This field will be printed on the Delivery Order and is required.
            </div>
          </Col>

          {/* Shipping Method, Prepared by, and Date in the same line */}
          <Col span={8} style={{ marginBottom: 16 }}>
            <label style={{ 
              display: "block", 
              marginBottom: 8, 
              fontSize: "14px", 
              fontWeight: 500 
            }}>
              Carrier
            </label>
            <div style={{ 
              padding: "12px 16px", 
              backgroundColor: "#f5f5f5", 
              borderRadius: "4px",
              minHeight: "40px",
              border: "1px solid #d9d9d9",
              fontSize: "14px"
            }}>
              {shippingMethod || "-"}
            </div>
          </Col>

          <Col span={8} style={{ marginBottom: 16 }}>
            <label style={{ 
              display: "block", 
              marginBottom: 8, 
              fontSize: "14px", 
              fontWeight: 500 
            }}>
              Prepared by
            </label>
            <div style={{ 
              padding: "12px 16px", 
              backgroundColor: "#f5f5f5", 
              borderRadius: "4px",
              minHeight: "40px",
              border: "1px solid #d9d9d9",
              fontSize: "14px"
            }}>
              Warehouse Team
            </div>
          </Col>

          <Col span={8} style={{ marginBottom: 16 }}>
            <Form.Item
              label={
                <span style={{ marginBottom: 8 }}>
                  Date
                </span>
              }
              name="date"
              style={{ marginBottom: 4 }}
            >
              <DatePicker
                size="large"
                format="YYYY-MM-DD"
                style={{ width: "100%",height: "48px" }}
                placeholder="Select date"
              />
            </Form.Item>
          </Col>
        </Row>
{/* 
        <div style={{ 
          fontSize: "12px", 
          color: "#666", 
          marginTop: 8,
          fontStyle: "italic"
        }}>
          Other all information from sales orders read only
        </div> */}
      </Form>
    </Modal>
  );
};

export default PrintDeliveryOrderModal;

